<template>
    <div>
        <div class="section-divider">
            <span class="section-title">Trade History</span>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-2">Loading trades...</p>
        </div>

        <div v-else-if="error" class="compass-info-box compass-info-box--danger">
            <p class="mb-0">{{ error }}</p>
        </div>

        <div v-else-if="trades.length === 0" class="compass-info-box">
            <p class="mb-0">No trades found for this wallet.</p>
        </div>

        <div v-else>
            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Exchange</th>
                            <th>Sold</th>
                            <th>Bought</th>
                            <th class="td-num">Value</th>
                            <th>Tx</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in trades" :key="t.signature + (t.instruction_index || '')">
                            <td>
                                <span class="text-muted small">{{ formatTime(t.time) }}</span>
                            </td>
                            <td>
                                <span class="compass-badge compass-badge--outline" style="font-size: 0.75rem;">
                                    {{ t.exchange }}
                                </span>
                            </td>
                            <td>
                                <div class="d-flex align-items-center gap-1">
                                    <span class="mono-num text-danger">
                                        {{ formatAmount(t.token_a.amount) }}
                                    </span>
                                    <a :href="`/tokens/${t.token_a.mint}`" class="text-decoration-none fw-semibold">
                                        {{ t.token_a.symbol || formatPubkey(t.token_a.mint) }}
                                    </a>
                                </div>
                            </td>
                            <td>
                                <div class="d-flex align-items-center gap-1">
                                    <span class="mono-num text-success">
                                        {{ formatAmount(t.token_b.amount) }}
                                    </span>
                                    <a :href="`/tokens/${t.token_b.mint}`" class="text-decoration-none fw-semibold">
                                        {{ t.token_b.symbol || formatPubkey(t.token_b.mint) }}
                                    </a>
                                </div>
                            </td>
                            <td class="td-num mono-num">
                                {{ formatTradeValue(t) }}
                            </td>
                            <td>
                                <a
                                    :href="`https://solscan.io/tx/${t.signature}`"
                                    target="_blank"
                                    class="font-mono text-muted small"
                                >{{ formatPubkey(t.signature, 4, 4) }}</a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.has_more || pagination.offset > 0">
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="pagination.offset === 0"
                    @click="loadPage(pagination.offset - pagination.limit)"
                >Previous</button>
                <span class="text-muted small">
                    Showing {{ pagination.offset + 1 }}-{{ Math.min(pagination.offset + trades.length, pagination.total) }}
                    of {{ pagination.total }}
                </span>
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="!pagination.has_more"
                    @click="loadPage(pagination.offset + pagination.limit)"
                >Next</button>
            </div>
        </div>
    </div>
</template>

<script>
import { formatPubkey } from '../composables/useFormatters';

export default {
    name: 'WalletTrades',
    props: {
        address: { type: String, required: true },
    },
    emits: ['loaded'],
    data() {
        return {
            trades: [],
            pagination: { offset: 0, limit: 50, total: 0, has_more: false },
            loading: true,
            error: null,
        };
    },
    methods: {
        formatPubkey,
        formatTime(time) {
            if (!time) return '';
            const d = new Date(time);
            const now = new Date();
            const diffDays = Math.floor((now - d) / 86400000);
            if (diffDays === 0) {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (diffDays < 7) {
                return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
            }
            return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        },
        formatAmount(val) {
            if (!val) return '0';
            if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B';
            if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (val >= 1e3) return (val / 1e3).toFixed(1) + 'K';
            if (val < 0.001) return val.toExponential(2);
            return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
        formatTradeValue(trade) {
            const valA = trade.token_a.value_usd || 0;
            const valB = trade.token_b.value_usd || 0;
            const val = Math.max(valA, valB);
            if (!val) return '-';
            if (val >= 1e6) return '$' + (val / 1e6).toFixed(2) + 'M';
            if (val >= 1e3) return '$' + (val / 1e3).toFixed(1) + 'K';
            return '$' + val.toFixed(2);
        },
        async fetchTrades(offset = 0) {
            this.loading = true;
            this.error = null;
            try {
                const resp = await fetch(`/api/v1/wallets/${this.address}/trades?limit=50&offset=${offset}`);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = await resp.json();
                const data = json.data || json;
                this.trades = data.trades || [];
                this.pagination = data.pagination || { offset: 0, limit: 50, total: 0, has_more: false };

                this.$emit('loaded', { total: this.pagination.total });
            } catch (e) {
                this.error = 'Failed to load trades. ' + e.message;
            } finally {
                this.loading = false;
            }
        },
        loadPage(offset) {
            this.fetchTrades(Math.max(0, offset));
        },
    },
    mounted() {
        this.fetchTrades();
    },
};
</script>
